import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * First-run guided tour. Spotlights real UI elements one at a time so new
 * users (guest or signed-in) see what the hub can do. Only shows once per
 * device; re-open any time from the profile page ("Show the tutorial").
 */
export const TOUR_SEEN_KEY = 'msk.tour-seen';

/** Re-opens the tour on any mounted page (used by the profile replay button). */
export function restartTour() {
  localStorage.removeItem(TOUR_SEEN_KEY);
  window.dispatchEvent(new Event('msk:tutorial-restart'));
}

type Step = { target?: string; title: string; body: string; icon?: string };

const STEPS: Step[] = [
  {
    title: 'Welcome aboard!',
    icon: '✦',
    body: 'A 20-second lap around the hub. Everything is free — and you can bail out at any point with the Skip button.'
  },
  {
    target: '[data-tour="course"]',
    title: 'Your courses live here',
    icon: '⇄',
    body: 'Click this chip to switch between courses. Your progress is tracked separately for each one.'
  },
  {
    target: '[data-tour="chapters"]',
    title: 'Chapters — your home base',
    icon: '❯',
    body: 'Every lecture video and worked note, organised chapter by chapter. Tick things off as you go and the progress bars fill up.'
  },
  {
    target: '[data-tour="tools"]',
    title: 'Handpicked tools',
    icon: '✎',
    body: 'Study tools your course actually recommends — including a Pomodoro timer that lives right in the app.'
  },
  {
    target: '[data-tour="ai"]',
    title: 'AI help',
    icon: '✧',
    body: 'Stuck on a problem? The AI tab is your way in for explanations and worked steps.'
  },
  {
    target: '[data-tour="streak"]',
    title: 'Streak & XP',
    icon: '⚡',
    body: 'Study one day, keep the flame. Earn XP for finished sections — visible here and on your profile.'
  },
  {
    target: '[data-tour="theme"]',
    title: 'Light or dark, your call',
    icon: '☾',
    body: 'This toggle flips between day and night mode and remembers your choice.'
  },
  {
    target: '[data-tour="profile"]',
    title: 'Your profile',
    icon: '◉',
    body: 'Pick it in the corner for stats, badges, week-by-week study time, and settings — including a way to replay this tour.'
  },
  {
    title: "You're all set",
    icon: '✓',
    body: 'That is the whole tour. Work through a chapter today to start your first streak. Good luck!'
  }
];

export function TutorialTour() {
  const [active, setActive] = useState(() => localStorage.getItem(TOUR_SEEN_KEY) !== '1');
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const raf = useRef(0);

  const finish = useCallback(() => {
    localStorage.setItem(TOUR_SEEN_KEY, '1');
    setActive(false);
  }, []);

  useEffect(() => {
    const restart = () => {
      setStep(0);
      setActive(true);
    };
    window.addEventListener('msk:tutorial-restart', restart);
    return () => window.removeEventListener('msk:tutorial-restart', restart);
  }, []);

  const stepDef = STEPS[step];

  // Keep the spotlight glued to its target (targets can appear late, scroll, or move on resize).
  useEffect(() => {
    if (!active) return;
    let last: string | null = null;
    setRect(null);
    const tick = () => {
      const el = stepDef?.target ? document.querySelector(stepDef.target) : null;
      const next = el ? JSON.stringify(el.getBoundingClientRect()) : null;
      if (next !== last) {
        last = next;
        setRect(el ? el.getBoundingClientRect() : null);
      }
      raf.current = window.setTimeout(tick, 120) as unknown as number;
    };
    tick();
    return () => window.clearTimeout(raf.current);
  }, [active, step, stepDef]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
      if (e.key === 'ArrowRight') setStep(s => Math.min(s + 1, STEPS.length - 1));
      if (e.key === 'ArrowLeft') setStep(s => Math.max(s - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, finish]);

  if (!active || !stepDef) return null;

  const pad = 10;
  const hasSpot = !!rect && rect.width > 0 && rect.height > 0;
  const last = step === STEPS.length - 1;
  const cardW = 340;

  // Place the card: centred for intro/outro, otherwise under the spotlight (or above when clipped).
  let cx: number;
  let cy: number;
  if (!hasSpot) {
    cx = (window.innerWidth - cardW) / 2;
    cy = window.innerHeight * 0.4;
  } else {
    const below = rect!.bottom + 16;
    const above = rect!.top - 16;
    cx = Math.min(window.innerWidth - cardW - 16, Math.max(16, rect!.left + rect!.width / 2 - cardW / 2));
    cy = below + 260 < window.innerHeight ? below : Math.max(16, above - 260);
  }

  return (
    <div className="tour-root" role="dialog" aria-modal="true" aria-label="Quick tour">
      {hasSpot ? (
        <>
          <div className="tour-block" />
          <div
            className="tour-spot"
            style={{
              left: rect!.left - pad,
              top: rect!.top - pad,
              width: rect!.width + pad * 2,
              height: rect!.height + pad * 2
            }}
          />
        </>
      ) : (
        <div className="tour-scrim" />
      )}
      <div className="tour-card" style={{ left: cx, top: cy, width: cardW }}>
        <div className="tour-head">
          {stepDef.icon && <span className="tour-icon" aria-hidden>{stepDef.icon}</span>}
          <h3>{stepDef.title}</h3>
        </div>
        <p>{stepDef.body}</p>
        <div className="tour-foot">
          <div className="tour-dots" aria-hidden>
            {STEPS.map((_, i) => (
              <span key={i} className={i === step ? 'on' : ''} />
            ))}
          </div>
          <div className="tour-nav">
            <button className="btn btn-ghost btn-sm" onClick={finish}>Skip</button>
            {step > 0 && (
              <button className="btn btn-outline btn-sm" onClick={() => setStep(s => s - 1)}>Back</button>
            )}
            <button className="btn btn-dark btn-sm" onClick={() => (last ? finish() : setStep(s => s + 1))}>
              {last ? 'Finish' : step === 0 ? 'Let’s go' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
